"use client";

import { useEffect, useRef, useState } from "react";
import { PortableText } from "@portabletext/react";

import { cn } from "@/lib/utils";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import type { PageFabioAffiliateSchema } from "@/sanity/pages/PageFabioAffiliateTemplate";

const NUMBER_POSITIONS = [
  "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2",
  "right-0 top-1/2 translate-x-1/2 -translate-y-1/2",
  "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2",
];

// Ring is drawn in a 100x100 viewBox, so r=50 → circumference = 2π*50.
const RING_RADIUS = 50;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default function StepsSection({ data }: { data: PageFabioAffiliateSchema }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const items = data?.stepsItems ?? [];
  const total = items.length;

  useEffect(() => {
    if (total === 0) return;

    const update = () => {
      rafRef.current = null;
      const node = wrapperRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const viewport = window.innerHeight;
      const scrollable = node.offsetHeight - viewport;
      if (scrollable <= 0) return;

      const scrolled = Math.min(Math.max(-rect.top, 0), scrollable);
      const p = scrolled / scrollable; // 0 → 1

      // Use rounding so the active text changes at the midpoint of each
      // segment, keeping it visually in sync with the progress ring.
      const next = Math.min(total - 1, Math.max(0, Math.round(p * (total - 1))));
      setProgress(p);
      setActiveIndex(next);
    };

    const handleScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [total]);

  if (
    !data?.stepsChip &&
    !data?.stepsHeading &&
    !data?.stepsRewardLabel &&
    !data?.stepsRewardValue &&
    !data?.stepsFounder &&
    !items.length &&
    !data?.stepsCTA
  ) {
    return null;
  }

  // Stroke offset goes from full circumference (empty) → 0 (full ring).
  const ringDashOffset = RING_CIRCUMFERENCE * (1 - progress);

  const headerBlock = (
    <>
      {data?.stepsChip && (
        <span className="italic leading-none text-black font-blacker text-[2rem] font-[400]">{data.stepsChip}</span>
      )}
      {data?.stepsHeading && (
        <div className="richtext richtext-center prose-h2:text-black prose-h2:text-[5rem] lg:prose-h2:text-[8rem] prose-h2:font-geist prose-h2:leading-[115%] prose-h2:tracking-[-0.2rem] lg:prose-h2:tracking-[-0.4rem] prose-h2:font-[500]">
          <PortableText
            value={data.stepsHeading}
            components={richTextComponent}
          />
        </div>
      )}
    </>
  );

  return (
    <section className="py-[5rem] lg:py-[10rem]">
      <div className="mx-auto w-[min(140rem,92%)]">
        <div
          ref={wrapperRef}
          style={total > 0 ? ({ "--step-count": total } as React.CSSProperties) : undefined}
          className="relative h-[calc(var(--step-count)*60vh)] lg:h-[calc(var(--step-count)*100vh)] ">
          <div className="flex sticky flex-col items-center top-[10rem] gap-[2rem] lg:gap-[5rem] lg:top-[5rem]">
            <div className="hidden flex-col items-center text-center lg:flex gap-[1rem] pb-[4rem]">{headerBlock}</div>
            <div className="flex flex-col items-center text-center lg:hidden gap-[1rem] mb-[6rem]">{headerBlock}</div>
            <div className="relative rounded-full border border-[#D7D7D7] w-[45rem] h-[45rem] lg:w-[77.5rem] lg:h-[77.5rem]">
              {/* Progress ring drawn on top of the static border */}
              <svg
                viewBox="0 0 100 100"
                className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
                aria-hidden="true">
                <circle
                  cx="50"
                  cy="50"
                  r={RING_RADIUS}
                  fill="none"
                  stroke="#0F7158"
                  strokeWidth="0.4"
                  strokeLinecap="round"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={ringDashOffset}
                />
              </svg>

              {items.slice(0, 3).map((step, i) => {
                const state = i < activeIndex ? "past" : i === activeIndex ? "active" : "future";
                return (
                  <div
                    key={`step-number-${step?.number ?? i}`}
                    className={cn(
                      "absolute rounded-full border lg:w-[8.5rem] lg:h-[8.5rem] p-[.5rem] border-[#001B14]/10 w-[6rem] h-[6rem]",
                      NUMBER_POSITIONS[i],
                    )}>
                    <div
                      style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
                      className={cn(
                        "flex justify-center items-center w-full h-full rounded-full transition-colors duration-[450ms] backdrop-blur-[2rem]",
                        state === "active" && "bg-[#0F7158]/20 ",
                        state === "past" && "bg-[#0F7158]/10 ",
                        state === "future" && "bg-[#001B14]/5 ",
                      )}>
                      <span className="italic leading-none tracking-[-0.1875rem] text-[#196551] font-[300] font-gestura text-[3rem] lg:text-[3.75rem]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                );
              })}

              <div className="flex absolute inset-0 justify-center items-center">
                <div className="relative w-[44.625rem] max-w-[80%] h-[30rem]">
                  {items.map((step, i) => {
                    const isActive = activeIndex === i;
                    return (
                      <div
                        key={`step-${step?.number ?? i}`}
                        style={{
                          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                          willChange: "opacity, transform",
                        }}
                        className={cn(
                          "absolute inset-0 flex flex-col justify-center items-center text-center transition-[opacity,transform] duration-[450ms]",
                          isActive
                            ? "opacity-100 translate-y-0 pointer-events-auto"
                            : "opacity-0 translate-y-[1.25rem] pointer-events-none",
                        )}>
                        {step?.icon && (
                          <GroqImage
                            src={step.icon as any}
                            alt=""
                            width={40}
                            height={40}
                            className="h-[4rem] w-[4rem]"
                          />
                        )}
                        {step?.title && (
                          <h3 className="text-[#1A1A1A] lg:text-[4rem] font-[500] text-center font-geist tracking-[0.08rem] leading-[110%] text-[3rem] ">
                            {step.title}
                          </h3>
                        )}
                        {step?.text && <p className="text-[2rem] text-[#666] lg:max-w-[70%]">{step.text}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
